import { usePostHog as usePostHogOriginal } from '../context/PostHogContext';
import { useCallback } from 'react';

/**
 * Custom hook for PostHog analytics with common tracking methods
 */
export const useAnalytics = () => {
  const postHogContext = usePostHogOriginal();

  // Track page views
  const trackPageView = useCallback((pageName, properties = {}) => {
    postHogContext?.trackPageView(pageName, properties);
  }, [postHogContext]);

  // Track button clicks
  const trackButtonClick = useCallback((buttonName, location, properties = {}) => {
    postHogContext?.trackEvent('button_clicked', {
      button_name: buttonName,
      location,
      ...properties,
    });
  }, [postHogContext]);

  // Track form submissions
  const trackFormSubmission = useCallback((formName, success = true, properties = {}) => {
    postHogContext?.trackEvent('form_submitted', {
      form_name: formName,
      success,
      ...properties,
    });
  }, [postHogContext]);

  // Track feature usage
  const trackFeatureUsage = useCallback((featureName, properties = {}) => {
    postHogContext?.trackEvent('feature_used', {
      feature_name: featureName,
      ...properties,
    });
  }, [postHogContext]);

  // Track navigation
  const trackNavigation = useCallback((from, to, properties = {}) => {
    postHogContext?.trackEvent('navigation', {
      from_page: from,
      to_page: to,
      ...properties,
    });
  }, [postHogContext]);

  // Track errors
  const trackError = useCallback((errorType, errorMessage, properties = {}) => {
    postHogContext?.trackEvent('error_occurred', {
      error_type: errorType,
      error_message: errorMessage,
      ...properties,
    });
  }, [postHogContext]);

  // Track user actions
  const trackUserAction = useCallback((action, properties = {}) => {
    postHogContext?.trackEvent('user_action', {
      action,
      ...properties,
    });
  }, [postHogContext]);

  return {
    // Direct access to PostHog context
    postHog: postHogContext?.posthog,
    isInitialized: postHogContext?.isInitialized,
    
    // Common tracking methods
    trackPageView,
    trackButtonClick,
    trackFormSubmission,
    trackFeatureUsage,
    trackNavigation,
    trackError,
    trackUserAction,
    
    // Direct event tracking
    trackEvent: postHogContext?.trackEvent,
    identifyUser: postHogContext?.identifyUser,
    resetUser: postHogContext?.resetUser,
  };
};

export default useAnalytics;
